import React, { useMemo } from 'react';
import { Table, Button } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { AnnualLeaveSummaryRow, LeaveTypeInfo } from '../../../services/pw9324Api';

interface Props {
  leaveTypes: LeaveTypeInfo[];
  data: AnnualLeaveSummaryRow[];
  count: number;
  loading: boolean;
  year: number;
  deptFrom: string;
  deptTo: string;
  onPrint: () => void;
}

/**
 * Annual leave summary table.
 * Columns: 員工代號 / 姓名 / 部門 + dynamic leave-type columns + 合計天數 / 合計時數.
 */
const LeaveSummaryTable = React.memo<Props>(
  ({ leaveTypes, data, count, loading, year, deptFrom, deptTo, onPrint }) => {
    const columns = useMemo<ColumnsType<AnnualLeaveSummaryRow>>(() => {
      const fixed: ColumnsType<AnnualLeaveSummaryRow> = [
        {
          title: '員工代號',
          dataIndex: 'emp_code',
          key: 'emp_code',
          width: 90,
          fixed: 'left',
        },
        {
          title: '姓名',
          dataIndex: 'emp_name',
          key: 'emp_name',
          width: 90,
          fixed: 'left',
        },
        {
          title: '部門',
          dataIndex: 'dept_name',
          key: 'dept_name',
          width: 100,
        },
      ];

      // Dynamic leave-type columns (BR-C05: up to 12 columns, 1-based slots)
      const dynamic: ColumnsType<AnnualLeaveSummaryRow> = leaveTypes.map((lt, idx) => ({
        title: lt.name,
        key: `leave_${lt.code}`,
        width: 80,
        align: 'right' as const,
        render: (_: unknown, row: AnnualLeaveSummaryRow) => {
          const slot = idx + 1; // slots 1..12
          const qty = row.leave_qty?.[slot] ?? 0;
          return qty !== 0 ? qty.toFixed(1) : '';
        },
      }));

      const tail: ColumnsType<AnnualLeaveSummaryRow> = [
        {
          title: '合計天數',
          dataIndex: 'd_tot',
          key: 'd_tot',
          width: 90,
          align: 'right' as const,
          render: (v: number) => (v != null ? v.toFixed(2) : ''),
        },
        {
          title: '合計時數',
          dataIndex: 'h_tot',
          key: 'h_tot',
          width: 90,
          align: 'right' as const,
          render: (v: number) => (v != null ? v.toFixed(1) : ''),
        },
      ];

      return [...fixed, ...dynamic, ...tail];
    }, [leaveTypes]);

    const rangeLabel = deptFrom || deptTo
      ? `部門 ${deptFrom || '(全部)'}～${deptTo || '(全部)'}`
      : '全部部門';

    return (
      <div>
        <div className="pw9324-table-header">
          <span className="pw9324-table-summary">
            {year} 年度請假總表 / {rangeLabel} / 共 {count} 人
          </span>
          <Button icon={<PrinterOutlined />} onClick={onPrint} size="small">
            列印
          </Button>
        </div>
        <Table<AnnualLeaveSummaryRow>
          columns={columns}
          dataSource={data}
          rowKey="emp_code"
          loading={loading}
          size="small"
          pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 人` }}
          scroll={{ x: Math.max(400, 280 + leaveTypes.length * 80 + 180) }}
          bordered
        />
      </div>
    );
  },
);

LeaveSummaryTable.displayName = 'LeaveSummaryTable';

export default LeaveSummaryTable;
